import React, { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "@/context/AuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import AdminLayout from "@/components/AdminLayout";
import { Toaster } from "@/components/ui/sonner";
import PdfViewerRoot from "@/components/PdfViewerModal";

import Login from "@/pages/Login";
import Dashboard from "@/pages/admin/Dashboard";
import Movies from "@/pages/admin/Movies";
import Showtimes from "@/pages/admin/Showtimes";
import Tickets from "@/pages/admin/Tickets";
import Reservations from "@/pages/admin/Reservations";
import POS from "@/pages/admin/POS";
import Snackbar from "@/pages/admin/Snackbar";
import Products from "@/pages/admin/Products";
import Inventory from "@/pages/admin/Inventory";
import Guests from "@/pages/admin/Guests";
import Vouchers from "@/pages/admin/Vouchers";
import Orders from "@/pages/admin/Orders";
import Betrieb from "@/pages/admin/Betrieb";
import Trivia from "@/pages/admin/Trivia";
import Kampagnen from "@/pages/admin/Kampagnen";
import Plakat from "@/pages/admin/Plakat";
import TicketDesigner from "@/pages/admin/TicketDesigner";
import DocumentDesigner from "@/pages/admin/DocumentDesigner";
import ContestsAdmin from "@/pages/admin/ContestsAdmin";
import Filmreihen from "@/pages/admin/Filmreihen";
import MarathonAdmin from "@/pages/admin/MarathonAdmin";
import Kassennachschau from "@/pages/admin/Kassennachschau";
import Kassensturz from "@/pages/admin/Kassensturz";
import Waitlist from "@/pages/admin/Waitlist";
import Flyers from "@/pages/admin/Flyers";
import KioskMarathonScan from "@/pages/kiosk/MarathonScan";
import Displays from "@/pages/admin/Displays";
import Newsletter from "@/pages/admin/Newsletter";
import Licht from "@/pages/admin/Licht";
import TVControl from "@/pages/admin/TVControl";
import Statistics from "@/pages/admin/Statistics";
import Analytics from "@/pages/admin/Analytics";
import SeatOrder from "@/pages/public/SeatOrder";
import Settings from "@/pages/admin/Settings";
import Users from "@/pages/admin/Users";
import Scanner from "@/pages/admin/Scanner";
import SaalKontrolle from "@/pages/admin/SaalKontrolle";
import WallpaperLibrary from "@/pages/admin/WallpaperLibrary";
import Halls from "@/pages/admin/Halls";
import PrerollAds from "@/pages/admin/PrerollAds";
import Abos from "@/pages/admin/Abos";
import PrintShop from "@/pages/admin/PrintShop";
import Reviews from "@/pages/admin/Reviews";
import NotfallRebook from "@/pages/admin/NotfallRebook";
import NotfallScan from "@/pages/public/NotfallScan";
import useWakeLock from "@/lib/useWakeLock";

import Program from "@/pages/public/Program";
import Reservation from "@/pages/public/Reservation";
import TicketView from "@/pages/public/TicketView";
import WebTicket from "@/pages/public/WebTicket";
import ClubCard from "@/pages/public/ClubCard";
import Kiosk from "@/pages/public/Kiosk";
import KioskTicket from "@/pages/public/KioskTicket";
import KinoIntro from "@/pages/public/KinoIntro";
import IntroRemote from "@/pages/public/IntroRemote";
import Landing from "@/pages/public/Landing";
import MarathonErfolg from "@/pages/public/MarathonErfolg";
import NewsletterPreference from "@/pages/public/NewsletterPreference";
import MemberLogin from "@/pages/public/MemberLogin";
import MemberDashboard from "@/pages/public/MemberDashboard";
import Shop from "@/pages/public/Shop";
import { PaymentSuccess, PaymentCancel } from "@/pages/public/PaymentResult";

import WelcomeDisplay from "@/pages/display/WelcomeDisplay";
import HallDisplay from "@/pages/display/HallDisplay";
import MenuDisplay from "@/pages/display/MenuDisplay";
import PickupDisplay from "@/pages/display/PickupDisplay";
import ProgramDisplay from "@/pages/display/ProgramDisplay";
import ThanksDisplay from "@/pages/display/ThanksDisplay";
import MovieHouseTV from "@/pages/display/MovieHouseTV";
import ProgramPreview from "@/pages/display/ProgramPreview";
import POSCustomerDisplay from "@/pages/display/POSCustomerDisplay";

function App() {
  useEffect(() => { document.title = "THE MOVIE HOUSE — Cinema OS"; }, []);
  useWakeLock();
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/login" element={<Login />} />

          {/* Public */}
          <Route path="/willkommen" element={<Navigate to="/" replace />} />
          <Route path="/program" element={<Program />} />
          <Route path="/reservieren/:showtimeId" element={<Reservation />} />
          <Route path="/ticket/:token" element={<TicketView />} />
          <Route path="/w/:ticketId" element={<WebTicket />} />
          <Route path="/reservierung/:token" element={<TicketView />} />
          <Route path="/kiosk" element={<Kiosk />} />
          <Route path="/kiosk/ticket/:showtimeId" element={<KioskTicket />} />
          <Route path="/kiosk/marathon" element={
            <ProtectedRoute roles={["admin","staff"]}>
              <KioskMarathonScan />
            </ProtectedRoute>
          } />
          <Route path="/intro/:showtimeId" element={<KinoIntro />} />
          <Route path="/remote/:showtimeId" element={<IntroRemote />} />
          <Route path="/club/:qr" element={<ClubCard />} />
          <Route path="/mitglied/login" element={<MemberLogin />} />
          <Route path="/mitglied" element={<MemberDashboard />} />
          <Route path="/shop" element={<Shop />} />
          <Route path="/marathon-erfolg" element={<MarathonErfolg />} />
          <Route path="/sitz/:showtimeId/:seatId" element={<SeatOrder />} />
          <Route path="/notfall-scan/:number" element={<NotfallScan />} />
          <Route path="/payment/success" element={<PaymentSuccess />} />
          <Route path="/payment/cancel" element={<PaymentCancel />} />
          <Route path="/newsletter/:token" element={<NewsletterPreference />} />

          {/* Displays (public, token-gated) */}
          <Route path="/display/welcome/:token" element={<WelcomeDisplay />} />
          <Route path="/display/hall/:token" element={<HallDisplay />} />
          <Route path="/display/menu/:token" element={<MenuDisplay />} />
          <Route path="/display/pickup/:token" element={<PickupDisplay />} />
          <Route path="/display/program/:token" element={<ProgramDisplay />} />
          <Route path="/display/thanks/:token" element={<ThanksDisplay />} />
          <Route path="/display/tv/:token" element={<MovieHouseTV />} />
          <Route path="/display/vorschau/:token" element={<ProgramPreview />} />
          <Route path="/display/pos/:displayKey" element={<POSCustomerDisplay />} />

          {/* Admin */}
          <Route path="/admin" element={
            <ProtectedRoute roles={["admin","staff","snack","door"]}>
              <AdminLayout />
            </ProtectedRoute>
          }>
            <Route index element={<Dashboard />} />
            <Route path="movies" element={<Movies />} />
            <Route path="showtimes" element={<Showtimes />} />
            <Route path="tickets" element={<Tickets />} />
            <Route path="reservations" element={<Reservations />} />
            <Route path="pos" element={<POS />} />
            <Route path="snackbar" element={<Snackbar />} />
            <Route path="products" element={<Products />} />
            <Route path="inventory" element={<Inventory />} />
            <Route path="guests" element={<Guests />} />
            <Route path="vouchers" element={<Vouchers />} />
            <Route path="orders" element={<Orders />} />
            <Route path="betrieb" element={<Betrieb />} />
            <Route path="trivia" element={<Trivia />} />
            <Route path="kampagnen" element={<Kampagnen />} />
            <Route path="plakat" element={<Plakat />} />
            <Route path="ticket-design" element={<TicketDesigner />} />
            <Route path="documents" element={<DocumentDesigner />} />
            <Route path="contests" element={<ContestsAdmin />} />
            <Route path="filmreihen" element={<Filmreihen />} />
            <Route path="marathon" element={<MarathonAdmin />} />
            <Route path="kassennachschau" element={<Kassennachschau />} />
            <Route path="kassensturz" element={<Kassensturz />} />
            <Route path="abos" element={<Abos />} />
            <Route path="druck-werkstatt" element={<PrintShop />} />
            <Route path="waitlist" element={<Waitlist />} />
            <Route path="flyers" element={<Flyers />} />
            <Route path="displays" element={<Displays />} />
            <Route path="statistics" element={<Statistics />} />
            <Route path="analytics" element={<Analytics />} />
            <Route path="settings" element={<Settings />} />
            <Route path="users" element={<Users />} />
            <Route path="scanner" element={<Scanner />} />
            <Route path="saal-kontrolle" element={<SaalKontrolle />} />
            <Route path="wallpapers" element={<WallpaperLibrary />} />
            <Route path="halls" element={<Halls />} />
            <Route path="werbung" element={<PrerollAds />} />
            <Route path="newsletter" element={<Newsletter />} />
            <Route path="licht" element={<Licht />} />
            <Route path="tv" element={<TVControl />} />
            <Route path="reviews" element={<Reviews />} />
            <Route path="notfall-rebook" element={<NotfallRebook />} />
          </Route>
        </Routes>
      </BrowserRouter>
      <PdfViewerRoot />
      <Toaster />
    </AuthProvider>
  );
}

export default App;
